import * as React from 'react'
import styled from 'styled-components'
import { ThemeName } from '../../../lib/constants'
import { typoHeading5, typoParagraphS } from '../../../lib/styles/Typography'
import { ButtonBlok, SbAsset } from '../../../types'
import { Button } from '../Button'

import { Card } from '../Card'
import { IconList } from '../IconList'
import { InfoList } from '../InfoList'
import CustomLink from '../../CustomLink'
import { IconListItem } from '../IconList/types'
import SourceForgeBadge from '../../../lib/SourceForgeBadge'
import { useMediaQuery } from '../../../lib/hooks/useMediaQuery'
import { useI18n } from '../../../lib/context/i18n'

export interface TeaserCardProps {
  assetHeight?: string
  assetWidth?: string
  isProductItem?: boolean
  href?: string
  to?: string
  asset?: SbAsset
  children: React.ReactNode
  step?: string
  title: string
  subtitle?: string
  products?: string
  icons?: IconListItem[]
  infos?: string[]
  justify?: 'left' | 'center'
  alignment?: 'left' | 'center'
  direction?: 'row' | 'column'
  themeBackground?: ThemeName
  themeSubtitle?: ThemeName
  noBorder?: boolean
  isSourceForge?: boolean
  buttons?: ButtonBlok[]
  titleFontSize?: string
  subtitleFontSize?: string
  contentFontSize?: string
}

const CardInner = styled.div<{
  justify?: 'left' | 'center'
  direction?: 'row' | 'column'
}>`
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: ${({ direction }) => direction || 'column'};
  align-items: ${({ justify }) => (justify === 'left' ? 'flex-start' : 'center')};
  text-align: ${({ justify }) => justify};
  gap: ${({ direction }) => direction === 'column' ? '1rem' : '0.5rem'};
  padding: ${({ direction }) => direction === 'column' ? '1rem' : '0.75em'};
  justify-content: space-between;
  border-radius: 0.5rem;

  // mobile
  @media screen and (max-width: 820px) {
    flex-direction: column;
    justify-content: flex-start;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
`

const AssetWrapper = styled.div<{
  justify: 'left' | 'center'
  assetHeight?: string
  assetWidth?: string
  direction?: 'row' | 'column'
}>`
  align-items: center;
  display: flex;
  justify-content: ${({ justify }) => (justify === 'left' ? 'flex-start' : 'center')};
  align-self: ${({ justify }) => (justify === 'left' ? 'flex-start' : 'center')};
  height: 100%;
  width: 100%;
  max-height: ${p => p.assetHeight || '4rem'};
  max-width: ${p => p.assetWidth || '4rem'};
`

const AssetImage = styled.img<{
  assetHeight?: string
  direction?: 'row' | 'column'
}>`
  display: block;
  max-height: ${p => p.direction === 'row' ? '100%' : p.assetHeight || '5rem'};
  max-width: 100%;
  height: 100%;
  width: 100%;
  
`

const CardTitle = styled.h5<{
  titleFontSize?: string
}>`
  font-size: ${p => p.titleFontSize || '1em'};
`

const CardSubtitle = styled.p<{
  $theme?: ThemeName
  subtitleFontSize?: string
}>`
  color: ${({ theme, $theme }) => theme.colors[$theme || 'black']?.['3'] || theme.colors.black['3']};
  font-size: ${p => p.subtitleFontSize || '1em'};
  font-weight: 600;
`
const CardContent = styled.div<{
  contentFontSize?: string
}>`
  font-size: ${p => p.contentFontSize || '1em'};
  margin: 0;
`

const CardFooter = styled.footer`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 2rem;
`

const CardContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  width: 100%;
`

const ButtonContainer = styled.div<{
  alignment?: 'left' | 'center'
}>`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-self: ${p => p.alignment === 'left' ? 'flex-start' : 'center'};
  justify-content: ${p => p.alignment === 'left' ? 'flex-start' : 'center'};
`

const ContentContainer = styled.div<{
  direction?: 'row' | 'column'
  justify?: 'left' | 'center'
}>`
  display: flex;
  width: 100%;
  align-items: center;
  flex-direction: ${({ direction }) => direction};
  gap: 1em;
`

export const TeaserCard = ({
  alignment = 'center',
  asset,
  children,
  icons,
  infos,
  justify = 'center',
  direction = 'column',
  step,
  title,
  subtitle,
  products,
  themeBackground,
  themeSubtitle,
  assetHeight,
  assetWidth,
  isProductItem,
  noBorder,
  isSourceForge,
  buttons,
  subtitleFontSize,
  titleFontSize,
  contentFontSize,
  ...rest
}: TeaserCardProps) => {
  const isLink = !!rest.href || !!rest.to
  const isExternal = !!rest.href && rest.href.startsWith('http')
  const isMobile = useMediaQuery('(max-width: 820px)')

  const { getTranslatedUrlFromLink } = useI18n()

  console.log(assetHeight, 'assetHeight')

  return (
    <Card
      $theme={themeBackground}
      as={isLink ? CustomLink : undefined}
      noBorder={true}
      {...(isExternal && { target: '_blank' })}
      {...(isExternal && { rel: 'nofollow noreferrer' })}
      {...rest}
    >
      <CardInner justify={justify} direction={direction}>
        <ContentContainer direction={direction} justify={justify}>
          {asset && asset.filename && (
            <AssetWrapper justify={justify} assetHeight={assetHeight} assetWidth={assetWidth} direction={direction}>
              {isSourceForge ? <SourceForgeBadge /> :
                <AssetImage src={asset.filename} alt={asset.alt} assetHeight={assetHeight} direction={direction} />}
            </AssetWrapper>
          )}
          <CardContentWrapper>
            {subtitle && <CardSubtitle $theme={themeSubtitle} subtitleFontSize={subtitleFontSize}>{subtitle}</CardSubtitle>}
            {title && <CardTitle titleFontSize={titleFontSize}>{title}</CardTitle>}
            {children && <CardContent contentFontSize={contentFontSize}>{children}</CardContent>}
          </CardContentWrapper>
        </ContentContainer>

        {icons && icons.length > 0 && (
          <CardFooter className="theme-reset">
            {icons && icons.length > 0 && (
              <IconList icons={icons} justify={justify === 'left' ? 'flex-start' : 'center'} />
            )}
            {infos && infos.length > 0 && (
              <InfoList text={infos} justify={justify === 'left' ? 'flex-start' : 'center'} />
            )}
          </CardFooter>
        )}
        <ButtonContainer alignment={alignment}>
          {buttons && buttons.length > 0 ? buttons?.map((button: ButtonBlok, index: number) => (
            <Button
              key={index}
              as={CustomLink}
              to={
                button.product_code
                  ? `${getTranslatedUrlFromLink(button.link)}?product_code=${button.product_code}`
                  : getTranslatedUrlFromLink(button.link)
              }
              $variant={button.button_style as 'primary' | 'secondary' | 'red' | 'green' | 'blue' | 'yellow'}
              $inverted={button.inverted}
              title={button.link_title}
              iconRight={button.iconRight}
              iconLeft={button.iconLeft}
              $size={button.size}
            >
              {button.button_name}
            </Button>
          )) : null}
        </ButtonContainer>
      </CardInner>
    </Card>
  )
}
